import type { PostProcessorModule, TOptions } from "i18next";

/**
 * i18next plugin that automatically applies Czech typography rules
 * Adds non-breaking spaces before prepositions and units
 */

const CZECH_PREPOSITIONS = ["a", "s", "k", "v", "z", "o", "u", "i"];
const UNITS = ["Kč", "měsíc", "měsíce", "měsíců", "rok", "roky", "let", "minut", "hodin", "dní"];
const ABBREVIATIONS = ["č.", "např.", "min.", "max.", "tzn.", "tj."];

export function applyCzechTypography<T extends string | string[]>(text: T): T {
  if (!text) {
    return text;
  }

  if (Array.isArray(text)) {
    if (text.every(t => typeof t === "string")) {
      return text.map(t => applyCzechTypography(t)) as T;
    }
    return text;
  }

  let result: string = text;

  // Replace regular space after single-letter prepositions with non-breaking space
  CZECH_PREPOSITIONS.forEach(prep => {
    // Match word boundary before preposition, the preposition itself (case insensitive), then a space
    const regex = new RegExp(`\\b(${prep})\\s`, "gi");
    result = result.replace(regex, `$1\u00A0`);
  });

  // Add non-breaking space before units (after numbers or interpolations)
  UNITS.forEach(unit => {
    const regex = new RegExp(`(\\d+|[^\\s]+)\\s+${unit}`, "g");
    result = result.replace(regex, `$1\u00A0${unit}`);
  });

  // Add non-breaking space after abbreviations
  ABBREVIATIONS.forEach(abbr => {
    const escapedAbbr = abbr.replace(".", "\\.");
    const regex = new RegExp(`${escapedAbbr}\\s+`, "g");
    result = result.replace(regex, `${abbr}\u00A0`);
  });

  // Replace regular hyphens with non-breaking hyphens in hyphenated words
  result = result.replace(/(\w)-(\w)/g, `$1\u2011$2`);

  return result as T;
}

export const czechTypographyPostProcessor: PostProcessorModule = {
  type: "postProcessor",
  name: "czechTypography",
  process(value: string, _key: string | string[], _options: TOptions, translator?: { language?: string }) {
    // Only apply to Czech language
    if (translator?.language === "cs") {
      return applyCzechTypography(value);
    }
    return value;
  },
};
